import type React from 'react';

import { Badge } from '~/components/ui/Badge';
import { Card } from '~/components/ui/Card';
import { Container } from '~/components/ui/Container';
import { Icon } from '~/components/ui/Icon';
import type { IconName } from '~/components/ui/Icon';
import { Reveal } from '~/components/ui/Reveal';
import { SectionHeader } from '~/components/ui/SectionHeader';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/**
 * Subtle diagonal scanline SVG pattern for the Railway card.
 * 3% opacity gives a faint "technical" texture without being distracting.
 * Static, no animation — purely decorative.
 */
function ScanlineTexture(): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="pointer-events-none select-none absolute inset-0 w-full h-full rounded-sm"
    >
      <defs>
        <pattern
          id="scanline-diag"
          x="0"
          y="0"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="6" stroke="#FF6B2B" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#scanline-diag)" opacity="0.03" />
    </svg>
  );
}

/**
 * Services — 6-card grid (3×2 desktop, 1-col mobile).
 * The last card (key: 'railway') uses Card variant="railway" and Rail Orange icon.
 * This is the ONLY place Rail Orange appears outside the RailwaySpecialisation section.
 */
function Services(): React.ReactElement {
  const content = useContent();
  const { services } = content;

  return (
    <section
      id="services"
      className={cn('bg-[var(--color-void-navy)]', 'py-24 md:py-32', 'scroll-anchor')}
      aria-labelledby="services-heading"
    >
      <Container>
        <SectionHeader
          eyebrow={services.eyebrow}
          heading={services.heading}
          lead={services.lead}
          align="left"
          id="services-heading"
          className="mb-14 md:mb-16 max-w-2xl"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.items.map((item, i) => {
            const isRailway = item.key === 'railway';

            return (
              <Reveal key={item.key} delay={i * 70}>
                <Card
                  variant={isRailway ? 'railway' : 'interactive'}
                  className="h-full flex flex-col relative overflow-hidden"
                  data-variant={isRailway ? 'railway' : 'interactive'}
                >
                  {/* Scanline texture overlay — railway card only, static, 3% opacity */}
                  {isRailway && <ScanlineTexture />}

                  {/* Railway badge — only on the railway card */}
                  {isRailway && (
                    <div className="mb-4 relative z-10">
                      <Badge variant="railway">EN 45545</Badge>
                    </div>
                  )}

                  {/* Icon — Cyan for default cards, Rail Orange for railway card */}
                  <div
                    className={cn(
                      'mb-4 relative z-10',
                      isRailway
                        ? 'text-[var(--color-rail-orange)]'
                        : 'text-[var(--color-cyan-signal)]'
                    )}
                    aria-hidden="true"
                  >
                    <Icon name={item.icon as IconName} size={40} />
                  </div>

                  {/* Title */}
                  <h3
                    className={cn(
                      'font-head font-semibold text-xl md:text-2xl tracking-[0.05em]',
                      'text-[var(--color-frost-white)]',
                      'mb-3 relative z-10'
                    )}
                  >
                    {item.title}
                  </h3>

                  {/* Body */}
                  <p
                    className={cn(
                      'font-body font-light leading-[1.8] text-base',
                      'text-[var(--color-steel-mist)]',
                      'flex-1 relative z-10'
                    )}
                  >
                    {item.body}
                  </p>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

export { Services };
export default Services;
